import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";

function GDriveSection({ token }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [driveStatus, setDriveStatus] = useState(null); // null | {connected, reason}
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [flashMsg, setFlashMsg] = useState(null);

  const fetchStatus = () => {
    fetch(`${API_BASE_URL}/api/gdrive/auth/status/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setDriveStatus)
      .catch(() => setDriveStatus({ connected: false, reason: "fetch_error" }));
  };

  useEffect(() => {
    fetchStatus();
    // Handle redirect-back from Google OAuth
    const gdrive = searchParams.get("gdrive");
    if (gdrive === "connected") {
      setFlashMsg({ type: "success", text: "Google Drive berhasil terhubung!" });
      setSearchParams({}, { replace: true });
      fetchStatus();
    } else if (gdrive === "error") {
      const reason = searchParams.get("reason") || "unknown";
      setFlashMsg({ type: "error", text: `Gagal menghubungkan Google Drive (${reason})` });
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/gdrive/auth/start/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal memulai OAuth");
      window.location.href = data.auth_url;
    } catch (e) {
      setFlashMsg({ type: "error", text: e.message });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Putuskan koneksi Google Drive?")) return;
    setDisconnecting(true);
    try {
      await fetch(`${API_BASE_URL}/api/gdrive/auth/status/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setFlashMsg({ type: "success", text: "Google Drive diputuskan." });
      fetchStatus();
    } catch {
      setFlashMsg({ type: "error", text: "Gagal memutuskan koneksi." });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="glass-card p-5 mb-8">
      <h2 className="text-lg font-semibold mb-1">Google Drive</h2>
      <p className="text-sm text-muted mb-4">
        Upload BAST & Order Panel otomatis ke Google Drive.
      </p>

      {flashMsg && (
        <div
          className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium ${
            flashMsg.type === "success"
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
          }`}
        >
          {flashMsg.text}
        </div>
      )}

      {driveStatus === null ? (
        <p className="text-sm text-muted">Mengecek status...</p>
      ) : driveStatus.connected ? (
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-sm font-medium">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="10" />
              <path d="M6 10l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            Terhubung
          </span>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="px-3 py-1 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
          >
            {disconnecting ? "Memutuskan..." : "Putuskan"}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />
            Belum terhubung
          </span>
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="px-4 py-1.5 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {connecting ? "Mengarahkan..." : "Hubungkan Google Drive"}
          </button>
        </div>
      )}
    </div>
  );
}

function DiscordSection({ token }) {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [status, setStatus] = useState(null);
  const [digestTime, setDigestTime] = useState("08:00");
  const [savingUrl, setSavingUrl] = useState(false);
  const [savingTime, setSavingTime] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [msg, setMsg] = useState(null);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const apiCall = async (body) => {
    const res = await fetch(`${API_BASE_URL}/api/settings/discord/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Gagal menyimpan.");
    return data;
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings/discord/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setStatus(data);
        const h = String(data.digest_hour ?? 8).padStart(2, "0");
        const m = String(data.digest_minute ?? 0).padStart(2, "0");
        setDigestTime(`${h}:${m}`);
      })
      .catch(() => {});
  }, [token]);

  const handleSaveUrl = async (e) => {
    e.preventDefault();
    setSavingUrl(true);
    try {
      const data = await apiCall({ discord_webhook_url: webhookUrl });
      setStatus(data);
      setWebhookUrl("");
      flash("success", "Webhook URL berhasil disimpan.");
    } catch (e) {
      flash("error", e.message);
    } finally {
      setSavingUrl(false);
    }
  };

  const handleSaveTime = async (e) => {
    e.preventDefault();
    const [h, m] = digestTime.split(":").map(Number);
    setSavingTime(true);
    try {
      const data = await apiCall({ digest_hour: h, digest_minute: m });
      setStatus(data);
      flash("success", `Jadwal laporan harian diubah ke ${digestTime}.`);
    } catch (e) {
      flash("error", e.message);
    } finally {
      setSavingTime(false);
    }
  };

  const handleBlast = async () => {
    setBlasting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/discord/test/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal mengirim.");
      flash("success", "Laporan stok dikirim ke Discord!");
    } catch (e) {
      flash("error", e.message);
    } finally {
      setBlasting(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Hapus webhook URL?")) return;
    setSavingUrl(true);
    try {
      const data = await apiCall({ discord_webhook_url: "" });
      setStatus(data);
      flash("success", "Webhook URL dihapus.");
    } catch {
      flash("error", "Gagal menghapus.");
    } finally {
      setSavingUrl(false);
    }
  };

  return (
    <div className="glass-card p-5 mb-8">
      <div className="flex items-center gap-2 mb-1">
        <svg className="w-5 h-5 text-indigo-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.032.054a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 13.88 13.88 0 0 0 1.226-1.995.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.127 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.994a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
        <h2 className="text-lg font-semibold">Discord Webhook</h2>
      </div>
      <p className="text-sm text-muted mb-5">
        Notifikasi stok habis real-time + laporan harian otomatis ke channel Discord.
      </p>

      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium ${
          msg.type === "success"
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
        }`}>
          {msg.text}
        </div>
      )}

      {/* ── Webhook URL ── */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2">Webhook URL</label>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {status === null ? (
            <span className="text-sm text-muted">Mengecek...</span>
          ) : status.webhook_set ? (
            <>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs font-medium">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="10"/>
                  <path d="M6 10l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                Aktif
              </span>
              <code className="text-xs text-muted bg-white/20 dark:bg-white/5 px-2 py-1 rounded">{status.webhook_preview}</code>
              <button onClick={handleClear} disabled={savingUrl}
                className="ml-auto text-xs px-2.5 py-1 rounded border border-red-300 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50">
                Hapus
              </button>
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400 text-xs font-medium">
              <span className="w-2 h-2 rounded-full bg-slate-400 inline-block"/>
              Belum dikonfigurasi
            </span>
          )}
        </div>
        <form onSubmit={handleSaveUrl} className="flex gap-2">
          <input
            type="url"
            className="flex-1 glass-input text-sm"
            placeholder="https://discord.com/api/webhooks/..."
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
          />
          <button type="submit" disabled={savingUrl || !webhookUrl.trim()}
            className="px-4 py-2 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-50">
            {savingUrl ? "Menyimpan..." : status?.webhook_set ? "Ganti" : "Simpan"}
          </button>
        </form>
      </div>

      {/* ── Jadwal Laporan Harian ── */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2">Jadwal Laporan Harian</label>
        <form onSubmit={handleSaveTime} className="flex items-center gap-3 flex-wrap">
          <input
            type="time"
            className="glass-input text-sm w-32"
            value={digestTime}
            onChange={(e) => setDigestTime(e.target.value)}
          />
          <button type="submit" disabled={savingTime}
            className="px-4 py-2 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-50">
            {savingTime ? "Menyimpan..." : "Simpan Jadwal"}
          </button>
          <span className="text-xs text-muted">
            Aktif: setiap hari {String(status?.digest_hour ?? 8).padStart(2,"0")}:{String(status?.digest_minute ?? 0).padStart(2,"0")}
          </span>
        </form>
      </div>

      {/* ── Kirim Sekarang ── */}
      <div>
        <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2">Kirim Laporan Sekarang</label>
        <button
          onClick={handleBlast}
          disabled={blasting || !status?.webhook_set}
          className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          {blasting ? "Mengirim..." : "Blast ke Discord"}
        </button>
        {!status?.webhook_set && (
          <p className="text-xs text-muted mt-1">Set webhook URL dulu untuk mengaktifkan.</p>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-white/10 text-xs text-muted space-y-0.5">
        <p>• <strong>Real-time:</strong> Auto kirim tiap kali stok komponen panel jadi 0.</p>
        <p>• <strong>Harian:</strong> Ringkasan otomatis sesuai jadwal di atas.</p>
        <p>• Cara buat webhook: Channel Discord → Edit → Integrations → Webhooks → New Webhook → Copy URL.</p>
      </div>
    </div>
  );
}

function Settings() {
  const [user, setUser] = useState(null);
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/auth/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setUser)
      .catch(() => {});
  }, [token]);

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Settings</h1>

      {/* --- Google Drive (superadmin only) --- */}
      {user?.role === "superadmin" && <GDriveSection token={token} />}
      {user?.role === "superadmin" && <DiscordSection token={token} />}

      {/* --- Maintenance / Danger Zone --- */}
      <div className="glass-card p-5">
        <h2 className="text-lg font-semibold mb-3">Maintenance</h2>
        <p className="text-sm text-muted mb-3">
          Rare actions such as removing items or resetting your test data.
        </p>

        <button
          className="px-4 py-2 bg-red-600 text-white rounded-md text-sm"
          onClick={() => alert("Later: open Remove Item modal here")}
        >
          Remove Item
        </button>
      </div>
    </div>
  );
}

export default Settings;
